import type { SxProps, Theme } from "@mui/material";
import type { DataGridProps, GridColDef, GridColumnVisibilityModel, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid";
import type { ReactNode } from "react";
import type { MultiSortOption, SortClickBehavior, SortingState } from "../../../../utils/multiColumnSorting";
import type { ExportFormat } from "./exportGridData";

/**
 * Spec for one searchable column.
 * - `keyof T` — the component does `String(row[key] ?? "")`, suitable for string/number columns.
 * - `{ field, getValue }` — caller supplies a string extractor for object/computed columns.
 */
export type SearchableColumn<T> = keyof T | { field: keyof T; getValue: (row: T) => string };

/** See `SystemDataGridProps.columnResetVisibility`. */
export type ColumnResetVisibility = "when-customized" | "always";

/** A flexible search field: as wide as the toolbar allows, between these widths (px). */
export type SearchWidthRange = { min: number; max: number };

/**
 * SystemDataGrid column def: a standard MUI `GridColDef` plus optional grid-specific flags. A plain
 * `GridColDef<T>` is still assignable (all extras are optional), so this is a non-breaking widening.
 */
export type SystemGridColDef<T extends GridValidRowModel> = GridColDef<T> & {
	/**
	 * Hide this column header's separator (the vertical divider on its right edge) at rest, revealing
	 * it on hover. Implemented via `headerClassName`; set this flag instead of touching the class.
	 */
	hideHeaderSeparator?: boolean;
	/**
	 * Rich hover card for the column header — typically the metric's plain-language definition. Rendered
	 * through `SortableDataGridHeader`'s `tooltipContent`, which is why it exists as a first-class prop:
	 * a custom header renderer suppresses MUI's native `description` tooltip, so `description` alone
	 * shows nothing on a sortable grid. Set both when you want the accessible fallback too.
	 */
	headerTooltip?: ReactNode;
	/**
	 * Override the value written for this column when the grid is exported (XLSX/CSV via `exportOptions`).
	 * Use it for computed or `renderCell`-only columns that have no plain `field`/`valueFormatter` value to
	 * serialize. When omitted, export falls back to `valueGetter` → `valueFormatter` → the raw cell value.
	 */
	getExportValue?: (row: T) => string | number | null;
	/** Exclude this column from data export (e.g. an actions column). */
	disableExport?: boolean;
	/**
	 * Keep this column out of the native column picker's list — it stays in the grid, it just can't be
	 * toggled. Use for identity columns (always visible), structural columns (per-day cells, an actions
	 * column), and trend companion columns whose visibility is driven by their parent metric instead.
	 */
	excludeFromColumnPicker?: boolean;
	/**
	 * The picker section this column belongs to. Only meaningful when `columnPicker.groupLabels` is set:
	 * the picker renders one section per group key. A plain string (not a union) so the shared component
	 * stays domain-agnostic — each consumer supplies its own group keys. Columns without a group (or with
	 * a group absent from `groupLabels`) are omitted from a grouped picker.
	 */
	columnPickerGroup?: string;
	/**
	 * The grouped-header band this column belongs to (the two-tier header — see `headerGroups`). Adjacent
	 * columns in DISPLAY order sharing the same key merge into one spanning band cell above them, and a
	 * reorder that separates them re-splits the band (MUI derives the merge/split from display order).
	 * Ungrouped columns (pinned identity columns) omit it and sit under a blank band cell. Distinct from
	 * `columnPickerGroup` (the picker's sections) — a column can carry both, for different purposes. Inert
	 * unless the grid is given `headerGroups`.
	 */
	headerGroup?: string;
};

/**
 * Config for the opt-in two-tier grouped header band (see `SystemGridColDef.headerGroup`). A labelled,
 * optionally-tinted band above the column headers; it persists nothing of its own (with reorder on, it
 * only steers where the picker drops a revealed column — see `SystemDataGridProps.headerGroups`). Kept separate from the column picker's `groupLabels` so a grid can group its
 * header band and its picker sections independently.
 */
export interface HeaderGroupsOptions {
	/**
	 * Pre-translated `groupKey → band label`. A group whose key is absent falls back to rendering the raw
	 * key (mirrors MUI's own `headerName ?? groupId`), so a missing translation shows the key, not a blank.
	 */
	labels: Record<string, string>;
	/**
	 * `groupKey → band-cell background`. Each value is a resolved color string the consumer builds from the
	 * theme (a token or `alpha(theme.palette…, x)`) — never a hardcoded literal. Groups with no entry keep
	 * the grid's default header tint. Ungrouped columns' blank band cells always keep the default tint.
	 */
	colors?: Record<string, string>;
}

/** One group as the grid resolved it, handed to `renderGroupLabel`. */
export interface RowGroup<T extends GridValidRowModel> {
	/** The key `getGroupKey` returned for every row in this bucket. */
	key: string;
	/**
	 * The group's rows, in the order search + sort left them. Always the FULL bucket, even when collapsed.
	 * Empty only for a `persistentGroupKeys` group that no displayed row landed in — the empty band.
	 */
	rows: T[];
	/** Whether the band is currently folded shut. */
	collapsed: boolean;
}

/**
 * What the band's host cell hands `renderGroupLabel` besides the group: its roving `tabIndex` (0 on the focused
 * cell, -1 otherwise). Put it on the label's own buttons so Tab leaves the grid instead of visiting every band;
 * the grid already puts it on the chevron in `toggleTarget: "chevron"` mode.
 */
export interface RowGroupCell {
	tabIndex: -1 | 0;
}

/**
 * Where a band's fold toggle lives (see `RowGroupingOptions.toggleTarget`).
 * - `"band"`: the whole strip is one button (chevron + label).
 * - `"chevron"`: only the chevron is a button; the label is a plain container that may hold its own
 *   controls (a rename pencil, a delete) without nesting buttons.
 */
export type RowGroupToggleTarget = "band" | "chevron";

/** The options every row-grouping config shares, whichever way its fold state is owned. */
interface RowGroupingBaseOptions<T extends GridValidRowModel> {
	/** The bucket a row belongs to. Rows returning the same key land under one band. */
	getGroupKey: (row: T) => string;
	/**
	 * The band's content, right of the chevron — already translated. Free-form so a group can carry a
	 * colour dot, a count or a chip; keep it a single line, since the band is one row tall.
	 *
	 * With `toggleTarget: "chevron"` this node sits in a plain `Box` that fills the rest of the band, so
	 * it may contain its own buttons and can push trailing controls to the band's far edge.
	 */
	renderGroupLabel: (group: RowGroup<T>, cell: RowGroupCell) => ReactNode;
	/**
	 * Explicit band order by key. Keys not listed here follow, in the order their first row appears —
	 * so a partial list is enough to pin the groups that have a meaningful order.
	 */
	groupOrder?: string[];
	/**
	 * Groups rendered WITHOUT a band: their rows sit in the grid as if ungrouped, in `groupOrder`
	 * position, so only the exceptional buckets get a band — e.g. everyone available listed plainly,
	 * then a folded "3 can't work this shift" band after them.
	 *
	 * A bandless group has no toggle, so it can never be folded: it is ignored in `defaultCollapsedKeys`,
	 * never reported by `onCollapsedChange`, and `renderGroupLabel` is never called for it.
	 */
	bandlessKeys?: string[];
	/**
	 * Groups that keep a band even when NO displayed row lands in them — e.g. every staffed shift window,
	 * so a window nobody works reads "No one scheduled" instead of silently vanishing from the list.
	 *
	 * A listed group that has rows is an ordinary band. One with none renders an **empty band**: muted,
	 * never foldable (no chevron, no click target, never reported by `onCollapsedChange`), and its
	 * `renderGroupLabel` is called with `rows: []` so the label can say so. Positioned by `groupOrder`
	 * like any other key; unpinned empty bands follow the populated ones, in this list's order.
	 *
	 * Empty bands are withheld while a search is active (a band exists only for rows that matched) and
	 * when the grid has no rows at all, so `emptyState` still owns the fully-empty grid. A key that is
	 * also in `bandlessKeys` has nothing to render when empty and is skipped.
	 */
	persistentGroupKeys?: string[];
	/**
	 * Reports which bands are currently folded shut, so a caller with a header-level bulk action can
	 * tell "every row that survived my filters" from "every row actually on screen" — a folded band's
	 * rows are the difference, and collapse state lives in the grid.
	 *
	 * Fires once on mount and then only when a band genuinely opens or closes (not on every row
	 * change). The set is **effective**, not raw: while a search is active every band renders expanded,
	 * so this reports `[]` regardless of what the user had folded. Keys are `getGroupKey` values.
	 */
	onCollapsedChange?: (collapsedKeys: string[]) => void;
	/** Set false for banded-but-not-foldable groups (no chevron, no click target). Default true. */
	collapsible?: boolean;
	/** Band row height in px. Defaults to 40 — one notch under the grid's own row height. */
	headerHeight?: number;
	/**
	 * Opt-in: one band's height in px, for bands whose content wraps onto more lines than others. Answers
	 * `headerHeight` when absent. Read through a ref, so a new function doesn't make the grid re-read every row's
	 * height (a fold toggle rebuilds the grouping config); when its ANSWERS change, call `apiRef.resetRowHeights()`.
	 */
	getHeaderHeight?: (group: RowGroup<T>) => number;
	/**
	 * Accessible name for a band's toggle, built from the group. Defaults to the grid's generic
	 * "Expand group"/"Collapse group" — pass this when the key alone doesn't name the group.
	 */
	getGroupAriaLabel?: (group: RowGroup<T>) => string;
	/**
	 * What folds the band when clicked. `"band"` (the default) makes the whole strip one button.
	 * `"chevron"` makes only the chevron a button — carrying `aria-expanded`, the `getGroupAriaLabel`
	 * name and the test id `${dataTestId}-group-${key}-toggle` — and renders the label as a plain
	 * container, so a band can hold its own buttons; the band then has no hover wash, since the strip is
	 * no longer a target.
	 */
	toggleTarget?: RowGroupToggleTarget;
	/**
	 * Opt-in (off by default): a sort orders the rows inside each band and never moves the bands. They keep the order
	 * their first rows arrive in `rows` (after any `groupOrder`), so a page that lists its groups in a meaningful order
	 * keeps it while the user sorts the rows under them. Off, a band sits where its first SORTED row falls, so sorting
	 * by a row column reorders the bands too.
	 */
	sortWithinGroups?: boolean;
}

/** The grid owns the fold state, seeded once from `defaultCollapsedKeys`. */
interface UncontrolledRowGroupCollapse {
	/** Groups folded shut on mount. The user's later toggles are grid state and are not persisted. */
	defaultCollapsedKeys?: string[];
	collapsedKeys?: never;
	onCollapsedKeysChange?: never;
}

/**
 * The caller owns the fold state: the grid renders `collapsedKeys` and a toggle only asks the caller
 * (via `onCollapsedKeysChange`) for the next set — it never folds a band on its own. For a page with
 * its own "collapse all" / "reveal this row" actions, which need the fold state outside the grid.
 */
interface ControlledRowGroupCollapse {
	/** The folded bands, by `getGroupKey`. Keys with no rows are ignored. A search still force-expands. */
	collapsedKeys: string[];
	/**
	 * Called with the RAW next set when the user toggles a band: `collapsedKeys` with that key added or
	 * removed (a removed key keeps the others' order; an added one is appended).
	 */
	onCollapsedKeysChange: (collapsedKeys: string[]) => void;
	defaultCollapsedKeys?: never;
}

/**
 * Config for opt-in collapsible **row grouping** (see `SystemDataGridProps.rowGrouping`).
 *
 * The grid buckets rows by `getGroupKey`, emits one full-width band per bucket, and owns the chevron
 * and the band's chrome. The caller supplies only the band's label node. The fold state is the grid's
 * (`defaultCollapsedKeys`) unless the caller controls it (`collapsedKeys` + `onCollapsedKeysChange`).
 */
export type RowGroupingOptions<T extends GridValidRowModel> = RowGroupingBaseOptions<T> & (UncontrolledRowGroupCollapse | ControlledRowGroupCollapse);

/** Narrows a grouping config to the controlled-collapse member. */
export function isControlledRowGroupCollapse<T extends GridValidRowModel>(
	options: RowGroupingOptions<T>,
): options is RowGroupingBaseOptions<T> & ControlledRowGroupCollapse {
	return options.collapsedKeys !== undefined;
}

/**
 * A picker row not backed by a single grid column: one checkbox that toggles a whole SET of real
 * column fields in lockstep. Use for a homogeneous block that should show/hide as one unit — e.g. a
 * single "Days" row controlling every per-day column (which each stay `excludeFromColumnPicker` so
 * they never list individually). The row reads checked when every controlled field is visible.
 */
export interface SyntheticPickerColumn {
	/** Stable identifier for the React key + testid. Never written to the visibility model. */
	id: string;
	/** Row label shown in the picker. */
	label: string;
	/** The real column `field`s this row toggles together. */
	controlledFields: string[];
	/** Section this row joins when the picker is grouped (`groupLabels`). */
	columnPickerGroup?: string;
}

export type ColumnPickerOptions = {
	/**
	 * The model the picker's "Reset" restores to (a curated all-on/some-off default). Also seeds the
	 * initial visibility on first load when nothing is persisted. Omit for "reset = show all" ({}).
	 */
	defaultVisibility?: GridColumnVisibilityModel;
	/**
	 * Extra picker rows not backed by a single grid column — each toggles a SET of real column fields
	 * in lockstep (see `SyntheticPickerColumn`). Rendered after the real pickable columns (or inside
	 * their `columnPickerGroup` when grouped).
	 */
	syntheticColumns?: SyntheticPickerColumn[];
	/**
	 * Maps a metric column's `field` to its companion trend column's `field`. When set, each mapped
	 * metric row shows an inline "Trend" checkbox: toggling the metric hides/shows both in lockstep,
	 * while the trend checkbox toggles only the companion (disabled when the metric is hidden). The
	 * trend column itself must be `excludeFromColumnPicker` so it never lists as a standalone row.
	 */
	trendColumnMatcher?: (field: string) => string | undefined;
	/**
	 * Pre-translated `groupKey → label`. Presence switches the picker to grouped mode; the record's key
	 * insertion order defines section order. Absent → a flat list of every non-excluded column.
	 */
	groupLabels?: Record<string, string>;
	/**
	 * Opt-in, grouped mode only (inert without `groupLabels`): each section header gets a tri-state
	 * "Select all" checkbox — checked when every entry listed in the section is visible, indeterminate when
	 * some are. Clicking it shows every listed entry (or hides them all when all are already visible). It acts
	 * on the entries CURRENTLY LISTED in that section, so under an active search it toggles only the matches,
	 * never the section's filtered-out rows. Trend companions follow their metric, as with a single toggle.
	 */
	groupSelectAll?: boolean;
	/**
	 * Fired with the resolved model on mount, on async-userId restore, and on every change — so consumers
	 * can drive out-of-grid concerns off the same model (e.g. sticky-column offsets, a custom CSV export).
	 */
	onModelChange?: (model: GridColumnVisibilityModel) => void;
	/**
	 * z-index for the picker's popover (it portals to `<body>`). Defaults to a value above MUI's modal
	 * layer so the picker isn't occluded inside a Dialog; raise it for deeper stacking contexts (nested modals).
	 */
	zIndex?: number | string;
	onlyShowIcon?: boolean;
};

/**
 * Where a row the sort doesn't order sits once the grid is sorted (see `SystemDataGridProps.getRowSortPin`):
 * - `after`: right under the row with this id, in the order such rows arrive in `rows`. A row under a row the grid
 *   doesn't show (searched or filtered out) is placed as `last`.
 * - `last`: after every sorted row, in arrival order; inside its band when the grid groups rows.
 */
export type RowSortPin = { kind: "after"; rowId: string | number } | { kind: "last" };

interface SystemDataGridBaseProps<T extends GridValidRowModel> {
	rows: T[];
	columns: SystemGridColDef<T>[];
	getRowId: (row: T) => string | number;

	/* ── Search ── */
	searchableColumns?: Array<SearchableColumn<T>>;
	searchPlaceholder?: string;
	/**
	 * Opt-in (off by default), row-expansion grids only (`getRowExpandContent`): when a non-empty search
	 * narrows the results to exactly one expandable row, that row opens automatically — no chevron click
	 * needed. Only ever OPENS a row (never closes one the user opened by hand, and never auto-collapses
	 * when the result set grows past one again). No-ops for cell expansion or non-expandable grids.
	 */
	autoExpandSingleSearchResult?: boolean;

	/* ── Sort ── */
	/** When provided, sortable headers + the sort-popup button are rendered. */
	sortingOptions?: MultiSortOption<T>[];
	defaultSortState?: SortingState<T>[];
	/**
	 * Fires with the current sort state on mount, on the async-userId restore, and on every sort change
	 * (header click or popup). Lets a caller that mirrors the layout outside the grid stay in sync — the
	 * sort analogue of `onColumnOrderChange`. Additive and off by default; grids that don't pass it are
	 * unaffected. Only meaningful alongside `sortingOptions`.
	 */
	onSortChange?: (sortState: SortingState<T>[]) => void;
	/**
	 * Opt-in (off by default, every row is sorted): the rows the sort carries rather than compares. A row this returns a
	 * pin for is never handed to a comparator; it rides under the row it names, or at the end (of its band, when
	 * grouped). For a row that belongs to another (an unsaved copy under its original) or that closes a list (an "Add"
	 * line). Return null for a row the sort orders. Applies only while the grid sorts (`sortingOptions`).
	 */
	getRowSortPin?: (row: T) => RowSortPin | null;
	/**
	 * Opt-in (off by default): sort from the column headers and their menus only, with no sort button in the toolbar.
	 * For a grid whose page carries its own toolbar, where a toolbar row holding one button would sit between the two.
	 * The popup's multi-key editing and "restore default" go with it; with `headerClickMode="replace"`, shift+click on a
	 * header still adds a tie-breaker.
	 */
	hideSortButton?: boolean;
	/** Persists sort state across reloads. Forwarded to both `useMultiSort` and `SystemSortingButtonWithPopup`. */
	localStorageKey?: string;
	/**
	 * Opt-in: when `sortingOptions` stops offering an axis (e.g. a column shown only in some modes), any
	 * sort on it is removed — from the live sort and the saved one — instead of ordering the rows with
	 * no row in the sort popup to clear it. Leave off for a grid that keeps an unlisted tie-breaker
	 * entry on purpose; this would drop it.
	 */
	dropUnofferedSorts?: boolean;
	/**
	 * Header-click cycle. Default cycles asc → desc → removed (the third click drops the column from the sort).
	 * Set true to keep a column sorted once clicked — the header just toggles asc ↔ desc, so the arrow never
	 * disappears (remove a sort via the sort popup instead). Only affects the clickable headers.
	 */
	keepSortOnHeaderToggle?: boolean;
	/**
	 * How a header click resolves against the sort keys already in state.
	 *
	 * Defaults to `"stack"`: an unsorted column is inserted as a new PRIMARY key and everything already
	 * there is demoted — three clicks on three headers leaving a three-key sort nobody asked for. It is
	 * the default only because it is what every existing grid shipped with.
	 *
	 * Pass `"replace"` — a plain click makes the clicked column the whole sort, shift+click appends it as
	 * a lower-priority tie-breaker — for the gesture a table header is usually expected to perform. It
	 * also implies `keepSortOnHeaderToggle`, since with a single key, dropping it on a third click would
	 * leave the grid in fetch order, which reads as a bug. Sort entries that no `sortingOptions` entry can
	 * rebuild (a grid's own invisible tie-breaker, e.g. Ops Backoffice's `stage` comparator) are preserved
	 * across a replace rather than silently dropped.
	 *
	 * Making `"replace"` the app-wide gesture is deliberately deferred: it can only be a real app-wide
	 * change once the hand-rolled tables move with it (StaffTable, MasterSchedule, DisciplinaryReport and
	 * DayViewGaps call `handleColumnSortClick` themselves and so stack regardless of what this prop says),
	 * and flipping the default here alone would just make the same gesture mean two things across pages.
	 */
	headerClickMode?: SortClickBehavior;
	/**
	 * Print the active sort in words under the grid — "Sorted by Role ↑, then Name ↑".
	 *
	 * A multi-key sort is otherwise legible only as superscript numerals in the headers, which is not
	 * something anyone reads. Spelling it out is also the only way a user notices they are three keys
	 * deep by accident.
	 */
	showSortSummary?: boolean;
	/**
	 * Adds a "Filter by <column>" item to the column menu for fields this returns true for, and calls
	 * `onFilterByColumn` when it is chosen — so a column can hand the user off to the page's filter
	 * rail instead of the grid growing a second, competing filter UI.
	 */
	canFilterByColumn?: (field: string) => boolean;
	onFilterByColumn?: (field: string) => void;
	/**
	 * Where a column's "Filter by…" lands, shown as the item's trailing hint — "in the rail", "in
	 * search". Only the page knows, because only the page owns the surface being jumped to, so a
	 * column that returns nothing gets no hint rather than a guessed one.
	 */
	filterByHint?: (field: string) => string | undefined;

	/* ── Column reorder ── */
	/**
	 * Opt-in (off by default): a hover-revealed drag handle on each column header lets users reorder
	 * columns, and a reset control appears in the toolbar once the order is customized. The order is
	 * persisted per-user when `localStorageKey` is set (the table's key must be allowlisted for sorting
	 * or column visibility); without a key it lives only for the session. The leading expand/toggle
	 * column (when expansion is enabled) is not reorderable and stays pinned first.
	 */
	enableColumnReorder?: boolean;

	/**
	 * When the toolbar's "Reset column order" control shows (needs `enableColumnReorder`).
	 * - `"when-customized"` (the default): only once the user has reordered — the toolbar stays lean.
	 * - `"always"`: always there, disabled (tooltip still readable) until the order is customized — for a grid
	 *   whose toolbar should keep one fixed shape, or that sits beside a grid whose reset is showing.
	 */
	columnResetVisibility?: ColumnResetVisibility;

	/**
	 * Opt-in (off by default): keep the toolbar's built-in controls — Sort, Export, Columns, any
	 * `trailingToolbarNodes` and Reset column order — together as one unit. When a crowded toolbar wraps, they
	 * move to the next line as a group (on that line's centre) instead of Sort staying behind while Columns drops.
	 */
	keepToolbarControlsTogether?: boolean;
	/**
	 * The Sort trigger as its icon alone (its label stays as the tooltip and accessible name), for a crowded
	 * toolbar. Pair it with `columnPicker.onlyShowIcon` so the two triggers match.
	 */
	sortOnlyShowIcon?: boolean;

	/**
	 * Fires with the new field order whenever the user reorders or resets columns (and on the async restore
	 * of a persisted order). Lets a caller that mirrors the layout outside the grid stay in sync.
	 */
	onColumnOrderChange?: (fieldOrder: string[]) => void;

	/**
	 * Opt-in (off by default): remember each column's manually-resized WIDTH. A resized width is reapplied
	 * onto the column definition so it survives both a `columns`-prop rebuild within the session (MUI
	 * otherwise resets resized widths when the columns array identity changes) and re-entering the page.
	 * Persisted per-user when `localStorageKey` is set (the key must be allowlisted for sorting or column
	 * visibility); without a key it lives only for the session.
	 */
	persistColumnWidths?: boolean;

	/* ── Column picker ── */
	/**
	 * Opt-in: renders a toolbar "Columns" popover that toggles per-column visibility. Its mere presence
	 * enables the feature (like `sortingOptions` enables sort). Visibility is owned by the grid and, when
	 * `localStorageKey` is set, persisted per-user under a `-columnVisibility` suffix (session-only without
	 * a key — same as reorder). Columns opt out of the list via `excludeFromColumnPicker`; group them with
	 * `columnPickerGroup` + `groupLabels`. While enabled, MUI's built-in column selector is suppressed.
	 */
	columnPicker?: ColumnPickerOptions;

	/* ── Row grouping ── */
	/**
	 * Opt-in: bucket rows into collapsible full-width group bands. Runs after search and sort, so a
	 * group keeps the sort's order, one with no matches disappears, and an active search force-expands
	 * every group (a collapsed group can never hide its own match). The grid owns the chevron; the
	 * collapse state is the grid's unless the caller controls it (`collapsedKeys` +
	 * `onCollapsedKeysChange`); the caller owns the label node.
	 *
	 * Not combinable with `checkboxSelection` — MUI's `__check__` column takes the span the band
	 * needs. Reported in dev; grouping steps aside.
	 *
	 * Bands are grid-injected and filtered out of every row/cell callback the grid forwards
	 * (`onRowClick`/`onRowDoubleClick`/`onCellClick`/`onCellDoubleClick`, `isRowSelectable`,
	 * `getRowSpacing`, `getCellClassName`, `getRowClassName`, `getRowHeight`, `isCellEditable`,
	 * `processRowUpdate`). Anything else
	 * reading `params.row` must check for itself — a band carries only `__groupKey` and the grid's own `__group`.
	 */
	rowGrouping?: RowGroupingOptions<T>;

	/* ── Grouped header band ── */
	/**
	 * Opt-in two-tier header: renders a labelled (optionally tinted) group band above the column headers.
	 * Each column declares its band via `headerGroup`; this supplies the per-group labels and colors.
	 * Consecutive same-group columns merge into one band cell and re-split on a reorder that separates
	 * them. Inert when no column sets `headerGroup`. Combined with `enableColumnReorder`, a column the
	 * `columnPicker` reveals that already sits next to a visible member of its band stays put; only an
	 * orphaned one moves, to just after its band's LARGEST visible run (the leftmost on a tie), persisted
	 * like a drag. A band with nothing visible yet leaves the column in place.
	 */
	headerGroups?: HeaderGroupsOptions;

	/* ── Editing (forwarded) ── */
	processRowUpdate?: DataGridProps<T>["processRowUpdate"];
	/**
	 * Called when `processRowUpdate` rejects. Pair it with an awaited/throwing `processRowUpdate` so
	 * the grid reverts the edited cell to its previous value on failure (MUI keeps the optimistic
	 * value otherwise). The row error is typically already surfaced by the mutation's own toast.
	 */
	onProcessRowUpdateError?: DataGridProps<T>["onProcessRowUpdateError"];
	isCellEditable?: DataGridProps<T>["isCellEditable"];

	/* ── Async state ── */
	/** When true, the grid body is replaced with skeleton rows (see `skeletonRowCount`). Toolbar still renders. */
	loading?: boolean;
	/**
	 * How many skeleton rows `loading` renders. Defaults to 3, which reads as a placeholder in a short
	 * table but as a mostly-empty page in a full-height one — pass a count that roughly fills the
	 * viewport there, so the shell doesn't visibly grow when the real rows land.
	 */
	skeletonRowCount?: number;
	/** When true, the grid body is replaced with an error message. Wins over `loading`. */
	error?: boolean;
	/** Optional override for the error copy. Defaults to a generic translated message. */
	errorMessage?: string;

	/**
	 * Reveal MUI's selection checkboxes on row hover (and keep them visible while a row is selected)
	 * instead of standing them permanently in the first column. Requires `dataGridProps.checkboxSelection`.
	 *
	 * The point is to retire "selection mode" as a state the user has to enter from a menu before they
	 * can tick anything: hover checkboxes cost no layout at rest, so selection can just always be
	 * available.
	 */
	hoverCheckboxSelection?: boolean;

	/**
	 * Sit the toolbar directly on top of the grid — no gap, with a divider under it — so the search
	 * box and the table read as one surface rather than two floating panels.
	 */
	flushToolbar?: boolean;

	/* ── Toolbar slots ── */
	leadingToolbarNodes?: ReactNode;
	trailingToolbarNodes?: ReactNode;
	/**
	 * A full-width line at the end of the toolbar — the searches or filters in effect, as chips — so they get a
	 * line of their own instead of crowding the controls. A footer that renders nothing takes no room.
	 */
	toolbarFooter?: ReactNode;
	toolbarSxOverride?: SxProps<Theme>;
	/**
	 * Width (px) of the search field. Defaults to 280 — narrow it when the toolbar row also carries wide
	 * nodes (a KPI strip) that would otherwise wrap onto a second line. A `{min, max}` range makes the field
	 * flex instead: it grows into the toolbar's spare room up to `max` and shrinks to `min` before the row wraps.
	 */
	searchWidth?: number | SearchWidthRange;

	/* ── Export ── */
	/**
	 * When provided, an export icon button appears in the toolbar with an XLSX/CSV menu. Exports the
	 * currently displayed rows (search + sort applied) in the visible column order. Columns can opt out
	 * via `disableExport` or override their serialized value via `getExportValue`. `formats` limits the
	 * offered formats (omitted = both); with a single format the button downloads it directly, no menu.
	 */
	exportOptions?: { fileName: string; formats?: readonly ExportFormat[] };

	/** Node for the no-rows overlay (e.g. an `EmptyState`) when not loading/error. Rendered with row expansion on, too. */
	emptyState?: ReactNode;

	/**
	 * Size the grid to its rows (between min/max, then scroll) instead of filling the parent — for
	 * sparse grids that otherwise leave a big blank body. `true` uses the defaults; the parent must
	 * not force a fixed height on the grid. Each bound is px, or any CSS length — e.g. a viewport-relative
	 * `calc(...)` token for a grid that caps at a share of the screen inside a content-sized popup.
	 */
	containedHeight?: boolean | { minHeight?: number | string; maxHeight?: number | string };

	/* ── Style escape hatches ── */
	/** Layered on top of the built-in compact-grid sx. */
	sx?: DataGridProps<T>["sx"];
	/**
	 * When true, cells keep MUI's default focus outline on click/focus.
	 * Defaults to false — most tables read better without the blue ring.
	 */
	showBorderOnCellFocus?: boolean;
	/**
	 * Initial rows-per-page for an EXPANDABLE grid (which uses a controlled pagination model). Falls
	 * back to the shared 50 default. Must be one of `pageSizeOptions` (25 / 50 / 100). For
	 * non-expandable grids, set page size via `dataGridProps.initialState` instead.
	 */
	defaultPageSize?: number;
	/**
	 * Forwarded to the underlying DataGrid for anything not exposed above.
	 *
	 * Checkbox row selection (`checkboxSelection` + `rowSelectionModel` + `onRowSelectionModelChange`)
	 * is fully supported **including while expansion is enabled**: detail rows are never selectable
	 * (they render no checkbox; the `__check__` column hosts the full-width detail cell), a header
	 * select-all is normalized to a concrete `include` model over the displayed (searched + sorted)
	 * rows before reaching your `onRowSelectionModelChange`, and `keepNonExistentRowsSelected`
	 * defaults to true under expansion so cross-page selections survive the internal server-mode
	 * pagination (pass it explicitly to opt out).
	 */
	dataGridProps?: Omit<Partial<DataGridProps<T>>, "rows" | "columns" | "getRowId" | "processRowUpdate" | "isCellEditable" | "sx">;

	/**
	 * Optional external grid `apiRef` (from `useGridApiRef()`). When provided, the SAME grid instance is
	 * used for both the internal column-anchored panel and the caller's imperative needs (e.g.
	 * `apiRef.current.scrollToIndexes(...)` to keep a keyboard cursor row in view). Falls back to an
	 * internally-created ref when absent — backward compatible. It cannot be passed via `dataGridProps`
	 * because the ref is applied to the live `DataGrid` after `{...dataGridProps}`.
	 */
	apiRef?: ReturnType<typeof useGridApiRef>;

	/** Base testid; the component appends suffixes (`-grid`, `-search`, `-sort-button`, etc.). */
	dataTestId?: string;
}

/**
 * Expansion is all-or-nothing AND single-mode: a grid is EITHER row-level (chevron) OR cell-level
 * (click a cell), never both. Each mode requires BOTH a content renderer AND a height function. We
 * deliberately require an explicit height rather than letting MUI auto-size detail rows —
 * auto-height attaches a per-row `ResizeObserver` that re-measures on every scroll/virtualization
 * tick, which (combined with the open/close animation) thrashes the grid into a render loop. A
 * fixed pixel height keeps scrolling cheap.
 *
 * Both modes share the SAME detail area below the row (injection + Collapse animation). The mode
 * is a discriminated union: each branch forbids the other mode's keys (`?: undefined`) so a caller
 * can't satisfy both at compile time. `isRowExpansion`/`isCellExpansion` narrow it at runtime.
 *
 * Row mode additionally supports LAZILY-LOADED rows — expandable, but their content has to be
 * fetched before they can open — via the optional `isRowExpandable` + `onRowExpandRequest` pair
 * below. Both default to today's behaviour when omitted.
 *
 * Note: while expansion is active the grid uses controlled, server-mode pagination internally
 * (this is a hack) — detail rows are injected only into the current page, so they always stay on
 * their parent's page, the footer count reflects the real rows (not detail rows), and expanded
 * rows persist across page changes.
 */
interface NoExpansionProps {
	getRowExpandContent?: undefined;
	getExpandContentHeight?: undefined;
	isRowExpandable?: undefined;
	onRowExpandRequest?: undefined;
	getRowExpandTooltip?: undefined;
	getCellExpandContent?: undefined;
	getCellExpandContentHeight?: undefined;
	expandedContentSx?: undefined;
	onExpandedRowsChange?: undefined;
	showExpandAll?: undefined;
	isolateExpandedContentKeys?: undefined;
	expandedRowIds?: undefined;
	onExpandedRowIdsChange?: undefined;
	getRowExpandAriaLabel?: undefined;
	hideCollapseAllRowsHeader?: undefined;
	rowExpandIndicator?: undefined;
	stableRowsOnToggle?: undefined;
}

/** Lazy loading off — the original synchronous contract, where content alone decides the chevron. */
interface EagerRowExpansion {
	isRowExpandable?: undefined;
	onRowExpandRequest?: undefined;
}

/**
 * Lazy loading on. The two props are a PAIR and the type says so: `isRowExpandable` advertises rows
 * whose content isn't there yet, and `onRowExpandRequest` is the only thing that can fetch it — one
 * without the other is a chevron whose click can never open anything, so it fails to compile rather
 * than rendering a dead control.
 */
interface LazyRowExpansion<T extends GridValidRowModel> {
	/**
	 * Decouples chevron visibility from content availability, which is what makes a lazily loaded row
	 * expressible. Omit the pair entirely to keep `getRowExpandContent(row) != null` as the rule.
	 *
	 * Return `true` for a row the user may open even though its content has to be fetched first; the
	 * chevron is drawn and the click routes to `onRowExpandRequest`.
	 */
	isRowExpandable: (row: T) => boolean;
	/**
	 * The lazy-load hook, called when the user opens an `isRowExpandable` row whose
	 * `getRowExpandContent` is still null. The grid shows a spinner in the chevron's place and does NOT
	 * open the row; it opens itself once your state change makes the content non-null. Load-then-expand,
	 * never an empty panel over an in-flight request.
	 *
	 * Resolve `true` when the fetch is under way, `false` when it failed or completed out of scope (the
	 * chevron returns to idle so the user can retry). Prefer not rejecting — a rejection is treated as
	 * `false`, but means the outcome never reached your own error handling.
	 *
	 * Clicks on a pending row are ignored, a second request is never started while the first is in
	 * flight, and pending rows are excluded from `autoExpandSingleSearchResult`.
	 *
	 * CONTRACT LIMITATION — no timeout, no cancel: the row spins for the life of the mount both if the
	 * promise never settles and if it resolves `true` but the content never arrives. Your handler owns
	 * the deadline and must resolve `false` in both cases.
	 */
	onRowExpandRequest: (row: T) => Promise<boolean>;
}

/** The grid owns which rows are open (the chevron and Collapse-all open and close rows on their own). */
interface UncontrolledRowExpansionState {
	expandedRowIds?: undefined;
	onExpandedRowIdsChange?: undefined;
}

/**
 * The caller owns which rows are open. The grid renders `expandedRowIds`, and the chevron, Collapse-all,
 * `autoExpandSingleSearchResult` and a lazy row's load-then-expand only ASK, through
 * `onExpandedRowIdsChange` — nothing opens or closes until the caller hands the ids back. For a page that
 * opens a row itself (a new draft row, an Edit action elsewhere), closes one after Save or Cancel, or must
 * intercept a close (unsaved changes). A change to `expandedRowIds` still animates: the grid reconciles it
 * through the same open/close path a click takes.
 */
interface ControlledRowExpansionState {
	/**
	 * The open rows, by `getRowId`. An id whose row isn't displayed right now (folded band, search, not
	 * loaded yet) stays open and shows its detail when the row comes back; one whose row never has content
	 * shows none. Compared by value, so a new array with the same ids is not a change.
	 */
	expandedRowIds: (string | number)[];
	/**
	 * Called with the RAW next set a user action asks for: `expandedRowIds` with the toggled id removed
	 * (the others keep their order) or appended; `[]` for Collapse-all. A page that allows one open row
	 * keeps only the newly added id.
	 */
	onExpandedRowIdsChange: (expandedRowIds: (string | number)[]) => void;
}

/** How the row chevron shows its state (see `rowExpandIndicator`). */
export type RowExpandIndicator = "swap" | "rotate";

interface RowExpansionBase<T extends GridValidRowModel> {
	/**
	 * Return a node to make `row` expandable: a chevron toggle appears in a leading column and the
	 * returned node renders in a full-width detail area below the row when expanded. Return null
	 * for rows that aren't expandable. Inactive while `loading` or `error`.
	 */
	getRowExpandContent: (row: T) => ReactNode | null;
	/**
	 * Full detail-ROW height in px for an expandable `row`. Must include any vertical padding
	 * from `expandedContentSx`; the default detail-cell padding adds 16px (`py: 1`) on top of
	 * your content. Used instead of MUI auto-height to keep scrolling cheap.
	 */
	getExpandContentHeight: (row: T) => number;
	/**
	 * Optional per-row override for the CLOSED chevron's tooltip and accessible name; `undefined` keeps
	 * the grid's wording. The open and loading states are always the grid's.
	 *
	 * Exists for the lazy row whose last request failed: the chevron is the only retry affordance, and
	 * "Load and expand row" doesn't say *try again*.
	 */
	getRowExpandTooltip?: (row: T) => string | undefined;
	/**
	 * Per-row accessible name for the chevron, in both states (`open` is the row's current state), so the
	 * control names what it opens — "Edit the Day shift" / "Close the Day shift" rather than "Expand row".
	 * An empty string or omitting it keeps the grid's wording. A lazy row's pending state keeps the grid's
	 * "Loading…" name. Does not add a tooltip; `getRowExpandTooltip` still owns the closed chevron's tooltip.
	 */
	getRowExpandAriaLabel?: (row: T, open: boolean) => string;
	/**
	 * Hide the Collapse-all button the chevron column's header shows while any row is open. For a page
	 * whose design has no such control, or that allows one open row, where it only duplicates the chevron.
	 */
	hideCollapseAllRowsHeader?: boolean;
	/**
	 * How the chevron shows the row's state. `"swap"` (the default) swaps a down arrow for an up arrow.
	 * `"rotate"` turns one chevron from pointing right (closed) to down (open), the same glyph and motion
	 * as a `rowGrouping` band's chevron, so a grid with both reads as one control family.
	 */
	rowExpandIndicator?: RowExpandIndicator;
	/**
	 * Keep the rows handed to MUI the same while a row only opens or closes in place, so a toggle re-renders
	 * the chevrons and the detail cell rather than every visible cell. Every prop of MUI's grid reaches every
	 * row and cell through one shared context, so a fresh rows array re-renders them all twice (once as the
	 * prop arrives, once as the grid re-syncs it). The rows still change when a detail row mounts or unmounts.
	 * A detail animating open or closed is also estimated where its animation starts (shut, or at its detail height)
	 * rather than at one standard row until it is measured, so the rows under it don't leave or mount for a frame. For a grid whose visible rows are
	 * costly to render. Off by default.
	 */
	stableRowsOnToggle?: boolean;
	getCellExpandContent?: undefined;
	getCellExpandContentHeight?: undefined;
	/**
	 * Layered on top of the detail area's default sx (`{ width: "100%", py: 1, px: 2 }`). Pass
	 * `{ py: 0 }` to remove the default vertical padding (and drop the 16px from your height
	 * calc), or any other sx to restyle the expanded content.
	 */
	expandedContentSx?: SxProps<Theme>;
	onExpandedRowsChange?: ExpandedRowsChangeHandler;
	/**
	 * Put an "Expand all" button in the chevron column's header while no row is open (it becomes the existing
	 * "Collapse all" once one is). Opens every displayed row whose content is already there; lazy rows are skipped,
	 * so it never fires a fetch per row.
	 */
	showExpandAll?: boolean;
	/**
	 * Keep keys typed inside the detail content away from the grid. Without it, the grid's cell navigation
	 * handles every arrow, Space, Home/End and PageUp/PageDown that bubbles out of the detail: it cancels
	 * them and moves focus to another row, so a text field can't move its caret or take a space (Space
	 * pages the grid down, which can scroll the open detail out of the DOM). Set it when the detail holds
	 * inputs, steppers or selects. Escape still bubbles, for a page-level handler. Keys on the detail's
	 * own cell (focus on the row, not inside the content) still navigate the grid. Off by default.
	 */
	isolateExpandedContentKeys?: boolean;
}

/**
 * Row-mode expansion: the always-present half, the lazy pair (all-or-nothing), and who owns the open rows
 * (the grid, or the caller through the controlled pair — also all-or-nothing).
 */
type RowExpansion<T extends GridValidRowModel> = RowExpansionBase<T> &
	(EagerRowExpansion | LazyRowExpansion<T>) &
	(UncontrolledRowExpansionState | ControlledRowExpansionState);

interface CellExpansion<T extends GridValidRowModel> {
	getRowExpandContent?: undefined;
	getExpandContentHeight?: undefined;
	isRowExpandable?: undefined;
	onRowExpandRequest?: undefined;
	getRowExpandTooltip?: undefined;
	/**
	 * Return a node to make the cell at (`row`, `field`) clickable-to-expand: clicking it opens the
	 * shared detail area below the row and renders the returned node there. Return null for cells
	 * that aren't expandable. There is ONE detail area per row — clicking a different expandable cell
	 * in the same row swaps the content in place (re-animating to the new height); clicking the same
	 * active cell closes it. No chevron column is rendered in this mode. Inactive while `loading`/`error`.
	 */
	getCellExpandContent: (row: T, field: keyof T) => ReactNode | null;
	/**
	 * Full detail-ROW height in px for the expanded content of (`row`, `field`). Same rationale and
	 * padding caveat as `getExpandContentHeight`. Different fields may return different heights — the
	 * row re-animates to the new height on swap.
	 */
	getCellExpandContentHeight: (row: T, field: keyof T) => number;
	/** Layered on top of the detail area's default sx. Same as the row-mode field. */
	expandedContentSx?: SxProps<Theme>;
	onExpandedRowsChange?: ExpandedRowsChangeHandler;
	showExpandAll?: undefined;
	/** Keep keys typed inside the detail content away from the grid. Same as the row-mode field. */
	isolateExpandedContentKeys?: boolean;
	expandedRowIds?: undefined;
	onExpandedRowIdsChange?: undefined;
	getRowExpandAriaLabel?: undefined;
	hideCollapseAllRowsHeader?: undefined;
	rowExpandIndicator?: undefined;
	stableRowsOnToggle?: undefined;
}

/**
 * Mirrors the OPEN rows out of the grid — the expansion analogue of `rowGrouping.onCollapsedChange`.
 * Expansion state lives inside the grid, so a cell that wants to look different while its own panel
 * is open (a pinned/turned chevron, a tinted value) has no way to know. Fires once on mount and on
 * every real open/close; ids only, in insertion order. Nothing is persisted.
 *
 * In CELL mode this is per ROW, not per cell: one row has one detail area, whichever of its cells
 * opened it.
 */
export type ExpandedRowsChangeHandler = (expandedRowIds: (string | number)[]) => void;

export type ExpansionProps<T extends GridValidRowModel> = NoExpansionProps | RowExpansion<T> | CellExpansion<T>;

/**
 * Search is a discriminated union, same reasoning as `ExpansionProps` above: `onSearchChange` alone is
 * valid (uncontrolled search, callback used only for analytics — filtering stays grid-owned), but
 * `searchValue` alone would leave the caller unable to ever change or clear what the grid displays, so
 * `onSearchChange` becomes REQUIRED the moment `searchValue` is passed (standard controlled-input contract).
 */
interface UncontrolledSearchProps {
	searchValue?: undefined;
	/** Fired on every search-box change (raw value). Consumers use it for analytics; filtering stays grid-owned when `searchValue` isn't also passed. */
	onSearchChange?: (value: string) => void;
}

interface ControlledSearchProps {
	/**
	 * Controls the search box's value from outside the grid — e.g. a nested row's "go to X" action
	 * writing a value into this grid's own search field. Omit for the grid's default self-contained
	 * behavior (search state lives internally).
	 */
	searchValue: string;
	/** Required alongside `searchValue` — the caller owns the value and MUST update it here to keep typing in the box working. */
	onSearchChange: (value: string) => void;
}

export type SearchProps = UncontrolledSearchProps | ControlledSearchProps;

/** Back-compat alias — existing callers import `RowExpansionProps`. Prefer `ExpansionProps`. */
export type RowExpansionProps<T extends GridValidRowModel> = ExpansionProps<T>;

/** Runtime narrowing to the row-level (chevron) branch. */
export function isRowExpansion<T extends GridValidRowModel>(p: ExpansionProps<T>): p is RowExpansion<T> {
	return p.getRowExpandContent != null;
}

/** Narrows row-mode expansion to the caller-controlled open rows. */
export function isControlledRowExpansion<T extends GridValidRowModel>(p: ExpansionProps<T>): p is RowExpansion<T> & ControlledRowExpansionState {
	return p.expandedRowIds !== undefined;
}

/** Runtime narrowing to the cell-level (click) branch. */
export function isCellExpansion<T extends GridValidRowModel>(p: ExpansionProps<T>): p is CellExpansion<T> {
	return p.getCellExpandContent != null;
}

export type SystemDataGridProps<T extends GridValidRowModel> = SystemDataGridBaseProps<T> & ExpansionProps<T> & SearchProps;
